<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <div class="d-flex justify-content-between align-items-center mb-0">
                <h5 class="mb-0">
                    <i class="fa fa-fw fa-users mr-2" />Daftar Peserta
                </h5>
                <b-button
                    variant="warning"
                    size="sm"
                    v-b-tooltip.hover.top
                    title="Reset Percobaan War"
                    @click="openResetAttemptsModal"
                >
                    <i class="fa fa-redo mr-1" />Reset Percobaan War
                </b-button>
            </div>
            <hr />

            <b-modal
                id="modal-reset-attempts"
                title="Reset Percobaan War"
                @ok="submitResetAttempts"
                :ok-title="isResetting ? 'Mereset...' : 'Reset'"
                cancel-title="Batal"
                ok-variant="warning"
                :ok-disabled="!resetForm.customer || isResetting"
                :cancel-disabled="isResetting"
                no-close-on-backdrop
                no-close-on-esc
            >
                <b-form-group label="Pilih Customer">
                    <multiselect
                        v-model="resetForm.customer"
                        label="name"
                        track-by="id"
                        placeholder="Cari customer..."
                        :internal-search="true"
                        deselect-label=""
                        select-label=""
                        :options="optCustomer"
                        :loading="isLoadingCustomers"
                    />
                </b-form-group>
            </b-modal>
            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive
                class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data peserta.
                </template>
                <template v-slot:cell(registered_at)="row">
                    <span>
                        {{
                            formatDateWithMs(
                                row.item.registered_at || row.item.created_at
                            )
                        }}
                    </span>
                </template>
                <template v-slot:cell(customer_name)="row">
                    {{ row.item.customer_name || "-" }}
                </template>
                <template v-slot:cell(customer_phone)="row">
                    {{ row.item.customer_phone || "-" }}
                </template>
                <template v-slot:cell(pax)="row">
                    {{ row.item.pax }}
                </template>
                <template v-slot:cell(slot)="row">
                    {{ row.item.slot }}
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusBadge(row.item.status)[0]">{{
                        getStatusBadge(row.item.status)[1]
                    }}</b-badge>
                </template>

                <template v-slot:cell(payment_status)="row">
                    <b-badge :variant="getPaymentStatusBadge(row.item.payment_status)[0]">{{
                        getPaymentStatusBadge(row.item.payment_status)[1]
                    }}</b-badge>
                </template>

                <template v-slot:cell(attendance_status)="row">
                    <b-badge :variant="getAttendanceStatusBadge(row.item.attendance_status)[0]">{{
                        getAttendanceStatusBadge(row.item.attendance_status)[1]
                    }}</b-badge>
                </template>

                <template v-slot:cell(achievement_status)="row">
                    <b-badge :variant="getAchievementStatusBadge(row.item.achievement_status)[0]">{{
                        getAchievementStatusBadge(row.item.achievement_status)[1]
                    }}</b-badge>
                </template>

                <template v-slot:cell(action)="row">
                    <div
                        class="d-flex justify-content-center align-items-center gap-1"
                    >
                        <b-button
                            v-if="row.item.status === SUBMISSION_STATUS.WAITLISTED"
                            size="sm"
                            variant="info"
                            class="text-white mr-1"
                            v-b-tooltip.hover.top
                            title="Ubah Status Menjadi Terdaftar"
                            @click="promote(row.item)"
                        >
                            <i class="fa fa-arrow-up" />
                        </b-button>
                        <b-button
                            v-if="row.item.status === SUBMISSION_STATUS.WAITLISTED"
                            size="sm"
                            variant="danger"
                            v-b-tooltip.hover.top
                            title="Batalkan Waitlist"
                            @click="removeWaitlist(row.item)"
                        >
                            <i class="fa fa-ban" />
                        </b-button>
                    </div>
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>
    </div>
</template>

<script>
import multiselect from "vue-multiselect";
import {
    SUBMISSION_STATUS,
    formatDateWithMs,
    getSubmissionStatusBadge as getStatusBadge,
    getPaymentStatusBadge,
    getAttendanceStatusBadge,
    getAchievementStatusBadge
} from "../konserHelper";

export default {
    components: { multiselect },
    data() {
        return {
            SUBMISSION_STATUS,
            initStatus: 0,
            isBusy: true,
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,
            items: [],
            concert: null,
            optCustomer: [],
            isLoadingCustomers: false,
            isResetting: false,
            resetForm: {
                customer: null,
            },
            allFields: [
                {
                    key: "registered_at",
                    label: "Tanggal Submit",
                    sortable: false
                },
                {
                    key: "customer_name",
                    label: "Nama",
                    sortable: false
                },
                {
                    key: "customer_phone",
                    label: "No. Telepon",
                    sortable: false
                },
                {
                    key: "pax",
                    label: "PAX",
                    sortable: false
                },
                {
                    key: "slot",
                    label: "Slot",
                    sortable: false
                },
                {
                    key: "status",
                    label: "Status Pendaftaran",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "payment_status",
                    label: "Status Pembayaran",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center",
                    requireDP: true
                },
                {
                    key: "attendance_status",
                    label: "Status Kehadiran",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center",
                    requireDP: true
                },
                {
                    key: "achievement_status",
                    label: "Pencapaian Target",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center",
                    requireDP: true
                },
                {
                    key: "action",
                    label: "Aksi",
                    sortable: false
                }
            ],
            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ]
        };
    },

    computed: {
        fields() {
            const hasDP = !this.concert || this.concert.require_down_payment !== false;
            return this.allFields.filter(f => !f.requireDP || hasDP);
        }
    },

    methods: {
        formatDateWithMs,
        getStatusBadge,
        getPaymentStatusBadge,
        getAttendanceStatusBadge,
        getAchievementStatusBadge,

        promote(item) {
            let isConfirmed = confirm(
                "Apakah Anda yakin ingin mengubah status peserta ini menjadi Terdaftar?"
            );
            if (!isConfirmed) return;

            this.isBusy = true;
            this.$axios
                .post(`concerts/submissions/${item.id}/promote`)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Status peserta berhasil diubah menjadi Terdaftar!"
                    );
                    this.getItems(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isBusy = false));
        },

        removeWaitlist(item) {
            let isConfirmed = confirm(
                "Apakah Anda yakin ingin membatalkan waitlist peserta ini?"
            );
            if (!isConfirmed) return;

            this.isBusy = true;
            this.$axios
                .post(`concerts/submissions/${item.id}/remove-waitlist`)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Waitlist peserta berhasil dibatalkan!"
                    );
                    this.getItems(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isBusy = false));
        },

        openResetAttemptsModal() {
            this.resetForm.customer = null;
            if (this.optCustomer.length === 0) {
                this.isLoadingCustomers = true;
                this.$axios
                    .get("customer/selection")
                    .then(res => {
                        this.optCustomer = res.data.data;
                    })
                    .catch(error => this.$helper.parseError(this, error))
                    .finally(() => (this.isLoadingCustomers = false));
            }
            this.$bvModal.show("modal-reset-attempts");
        },

        submitResetAttempts(bvModalEvent) {
            bvModalEvent.preventDefault();
            if (!this.resetForm.customer || this.isResetting) return;

            const concertId = this.$route.params.id;
            const customerId = this.resetForm.customer.id;

            this.isResetting = true;
            this.$axios
                .delete(`concerts/${concertId}/customers/${customerId}/attempts`)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Percobaan war peserta berhasil direset!"
                    );
                    this.$bvModal.hide("modal-reset-attempts");
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isResetting = false));
        },

        init() {
            this.initStatus = 0;
            Promise.all([this.getConcert(), this.getItems()])
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        getConcert() {
            return this.$axios
                .get(`concerts/${this.$route.params.id}`)
                .then(res => {
                    this.concert = res.data.data;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                    throw error;
                });
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage
            );

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy;
                param += "&sortDesc=" + this.sortDesc;
            }

            return this.$axios
                .get(`concerts/${this.$route.params.id}/submissions` + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.getItems();
        }
    },

    created() {
        this.init();
    }
};
</script>
